import React, { useCallback, useEffect, useState } from 'react';
import { FEEDBACK_TAGS, fetchFeedbackExperiences } from '../utils/platformFeedback';
import './PlatformExperiencesModal.css';

const TAG_LABELS = Object.fromEntries(FEEDBACK_TAGS.map((t) => [t.id, t.label]));

function formatExperienceDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function renderStars(score) {
  const n = Math.min(5, Math.max(0, Math.round(Number(score) || 0)));
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

const PlatformExperiencesModal = ({ open, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setIndex(0);
    fetchFeedbackExperiences({ limit: 100 }).then((data) => {
      if (cancelled) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const total = items.length;
  const current = total > 0 ? items[index] : null;

  const goPrev = useCallback(() => {
    setIndex((i) => (i <= 0 ? total - 1 : i - 1));
  }, [total]);

  const goNext = useCallback(() => {
    setIndex((i) => (i >= total - 1 ? 0 : i + 1));
  }, [total]);

  useEffect(() => {
    if (!open || total === 0) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, total, goPrev, goNext, onClose]);

  if (!open) return null;

  return (
    <div
      className="platform-experiences-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="platform-experiences-title"
      onClick={onClose}
    >
      <div className="platform-experiences-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="platform-experiences-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <h2 id="platform-experiences-title" className="platform-experiences-title">
          Shopper experiences
        </h2>
        <p className="platform-experiences-sub">
          What people have shared about using Raheasy.
        </p>

        {loading ? (
          <p className="platform-experiences-status">Loading experiences…</p>
        ) : total === 0 ? (
          <p className="platform-experiences-status">No experiences to show yet.</p>
        ) : (
          <>
            <div className="platform-experiences-card">
              <div className="platform-experiences-stars" aria-hidden="true">
                {renderStars(current.score)}
              </div>
              <p className="platform-experiences-score">
                {Number(current.score).toFixed(1)} / 5
              </p>
              <blockquote className="platform-experiences-quote">
                {current.comment
                  ? `"${current.comment}"`
                  : 'Rated their experience on Raheasy.'}
              </blockquote>
              {current.tags?.length > 0 && (
                <div className="platform-experiences-tags">
                  {current.tags.map((tag) => (
                    <span key={tag} className="platform-experiences-tag">
                      {TAG_LABELS[tag] || tag}
                    </span>
                  ))}
                </div>
              )}
              {formatExperienceDate(current.createdAt) && (
                <time className="platform-experiences-date" dateTime={current.createdAt}>
                  {formatExperienceDate(current.createdAt)}
                </time>
              )}
            </div>

            <div className="platform-experiences-nav">
              <button
                type="button"
                className="platform-experiences-nav-btn"
                onClick={goPrev}
                aria-label="Previous experience"
              >
                ‹
              </button>
              <span className="platform-experiences-counter">
                {index + 1} of {total}
              </span>
              <button
                type="button"
                className="platform-experiences-nav-btn"
                onClick={goNext}
                aria-label="Next experience"
              >
                ›
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlatformExperiencesModal;
